import { useId } from "react";
import { useTranslation } from "react-i18next";
import type { CandidateListing, ReviewUnit } from "../../../api/content/curationApi";
import type { Review } from "../../../api/content/reviewDecoders";
import CandidateBlock from "../candidates/CandidateBlock";
import { candidatesForTerm } from "../candidates/candidateModel";
import { conflictRows } from "../conflicts/conflictRows";
import ConflictRowView from "../conflicts/ConflictRowView";
import type { OutlineGroupKey } from "../outline/reviewOutline";
import TripleList from "../page/TripleList";
import { allows } from "../state/decisionRules";
import RenameTerm from "./RenameTerm";
import TechnicalDetails from "./TechnicalDetails";
import { plainTermText, termFieldChanges } from "./termFieldChanges";
import { changeToneClass } from "../../../ui/styles/changeStyles";
import { classNames } from "../../../ui/styles/classNames";
import EmptyState from "../../../ui/feedback/EmptyState";
import { useReviewTermNames } from "../conflicts/conflictTermNames";

/**
 * One term of the change set (curation-ui.md CU-3): a sentence first, then each field
 * before and after, its possible duplicates (CU-6), and the connected items that use it.
 * The N-Triples stay behind "Technical details".
 */
export default function TermUnit({
  unit,
  group,
  name,
  review,
  candidates,
  canEdit,
  technical,
  onFocusItem,
  onChanged,
}: {
  unit: ReviewUnit;
  group: OutlineGroupKey;
  name: string;
  review: Review;
  candidates: CandidateListing | null;
  canEdit: boolean;
  technical: boolean;
  onFocusItem: (itemId: string) => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const labels = useReviewTermNames(review);
  const changes = termFieldChanges(unit, labels);
  const term = unit.term ?? "";
  const kind = t(`reviews.curation.kinds.${group}`);
  const parents = changes.addedParents.map(plainTermText).join(", ");
  const findings = (review.conflictCheck?.findings ?? []).filter((finding) => finding.term === term && finding.item !== null);
  const changedItemIds = new Set(review.items.map((item) => item.itemId));
  const views = candidatesForTerm(candidates, term);
  return (
    <div className="space-y-4">
      <p className="text-base">
        {t(`reviews.curation.sentence.${unit.change ?? "changed"}`, { kind, name })}
        {parents !== "" ? t("reviews.curation.sentence.parents", { names: parents }) : null}
        <span className="app-text-muted block font-mono text-xs">{term}</span>
      </p>
      {changes.fields.length > 0 ? (
        <table className="app-table w-full table-fixed text-sm">
          <caption className="sr-only">{t("reviews.curation.fieldsCaption", { name })}</caption>
          <thead>
            <tr className="app-table-head-row text-left text-xs">
              <th scope="col" className="app-table-head w-32 px-2 py-1">{t("reviews.curation.compare.field")}</th>
              <th scope="col" className="app-table-head px-2 py-1">{t("reviews.curation.before")}</th>
              <th scope="col" className="app-table-head px-2 py-1">{t("reviews.curation.after")}</th>
            </tr>
          </thead>
          <tbody>
            {changes.fields.map((field) => (
              <tr key={field.predicate} className="app-table-row align-top">
                <th scope="row" className="app-table-cell px-2 py-1 text-left font-medium" title={field.predicate}>
                  {field.predicateKey !== null ? t(`reviews.predicates.${field.predicateKey}`) : field.predicateName}
                </th>
                <td className="app-table-cell px-2 py-1">
                  {field.before.length > 0 ? <span className={classNames(changeToneClass("removed"), "rounded px-1")}>{field.before.map(plainTermText).join(", ")}</span> : <span className="app-text-muted">—</span>}
                </td>
                <td className="app-table-cell px-2 py-1">
                  {field.after.length > 0 ? <span className={classNames(changeToneClass("added"), "rounded px-1")}>{field.after.map(plainTermText).join(", ")}</span> : <span className="app-text-muted">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
      <TripleList title={t("reviews.curation.otherAdded", { count: changes.otherAdded.length })} lines={changes.otherAdded} labels={labels} tone="added" />
      <TripleList title={t("reviews.curation.otherRemoved", { count: changes.otherRemoved.length })} lines={changes.otherRemoved} labels={labels} tone="removed" />
      {unit.change === "added" || views.length > 0 ? (
        <CandidateBlock
          key={`${review.head}:${term}`}
          views={views}
          modules={candidates?.modules ?? []}
          head={review.head}
          changeSetId={review.changeSet.changeSetId}
          projectId={review.changeSet.projectId}
          termIri={term}
          termName={name}
          labels={labels}
          canEdit={canEdit}
          canPropose={allows(review, "propose_candidate")}
          onChanged={onChanged}
        />
      ) : null}
      <section className="space-y-1" aria-labelledby={headingId}>
        <h4 id={headingId} className="app-subsection-title text-sm">{t("reviews.curation.usedBy", { count: findings.length })}</h4>
        {findings.length === 0 ? <EmptyState>{t("reviews.curation.usedByNone")}</EmptyState> : null}
        <ul className="space-y-1">
          {conflictRows(findings).map((row) => (
            <ConflictRowView
              key={row.kind === "single" ? `${row.level}:${row.finding.item?.itemId ?? ""}:${row.finding.kind}` : `${row.level}:${row.item.itemId}`}
              row={row}
              labels={labels}
              changedItemIds={changedItemIds}
              onFocusItem={onFocusItem}
            />
          ))}
        </ul>
      </section>
      {canEdit && unit.change !== "removed" ? <RenameTerm review={review} term={term} onChanged={onChanged} /> : null}
      {technical ? <TechnicalDetails added={unit.addedTriples} removed={unit.removedTriples} addedCount={unit.addedCount} removedCount={unit.removedCount} /> : null}
    </div>
  );
}
